import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "editableCell",
    "editButton",
    "tableContainer",
    "realizedCheckbox",
  ]
  static values = {
    editMode: Boolean,
  }

  connect() {
    this.currentSort = null
    this.currentSortDirection = null

    // Initialize realized checkboxes on page load
    this.initializeRealizedCheckboxes()
  }

  initializeRealizedCheckboxes() {
    document.querySelectorAll(".realized-checkbox").forEach((checkbox) => {
      const isRealized = checkbox.dataset.realized === "true"
      if (isRealized) {
        checkbox.classList.add("realized-checkbox--checked")

        // Also disable other checkboxes in the same row and add grey background
        const row = checkbox.closest("tr")
        if (row) {
          row.querySelectorAll(".realized-checkbox").forEach((cb) => {
            if (cb !== checkbox) {
              cb.classList.add("realized-checkbox--disabled")
            }
          })

          // Add grey background to cells before the realized quarter
          const cells = Array.from(row.querySelectorAll("td"))
          const currentCell = checkbox.closest("td")
          const currentCellIndex = cells.indexOf(currentCell)

          cells.forEach((cell, index) => {
            if (index > 0 && index < currentCellIndex) {
              cell.classList.add("realized-future-cell")
            }
          })
        }
      }
    })
  }

  toggleEdit(e) {
    e.preventDefault()

    if (this.editModeValue) {
      // Save mode - collect data and submit
      const distributionRows = document
        .querySelector("#distribution-allocation tbody")
        .querySelectorAll(
          "tr:not(.totals-row):not(.statement-row):not(.other-row)",
        )

      const distributions = []
      const otherBalanceSheetItems = []
      const realizations = []

      distributionRows.forEach((row) => {
        const cells = row.querySelectorAll("td")
        if (cells.length >= 2) {
          const portfolioCompanyId = row.dataset.portfolioCompanyId
          const investmentAssetId = row.dataset.investmentAssetId

          // Skip if no portfolio company ID
          if (!portfolioCompanyId) return

          // Track if we found a realized checkbox for this row
          let realizedYear = null
          let realizedQuarter = null

          // Process each quarter cell
          for (let i = 1; i < cells.length; i++) {
            const inputGroup = cells[i].querySelector(".input-group")
            if (inputGroup) {
              const input = inputGroup.querySelector("input")
              const yearQuarter = inputGroup.dataset.yearQuarter
              if (input && yearQuarter) {
                const [year, quarter] = yearQuarter.split("-")
                distributions.push({
                  portfolio_company_id: portfolioCompanyId,
                  investment_asset_id: investmentAssetId,
                  year: parseInt(year),
                  quarter: parseInt(quarter),
                  amount: parseFloat(input.value || 0),
                })

                // Check if this cell has a realized checkbox that's checked
                const realizedCheckbox =
                  inputGroup.querySelector(".realized-checkbox")
                if (
                  realizedCheckbox &&
                  realizedCheckbox.classList.contains(
                    "realized-checkbox--checked",
                  )
                ) {
                  realizedYear = parseInt(year)
                  realizedQuarter = parseInt(quarter)
                }
              }
            }
          }

          // If we found a realized checkbox for this row, add it to realizations
          if (realizedYear && realizedQuarter) {
            realizations.push({
              portfolio_company_id: portfolioCompanyId,
              investment_asset_id: investmentAssetId,
              year: realizedYear,
              quarter: realizedQuarter,
              realized: true,
            })
          } else {
            // If no checked checkbox, send realized: false to clear any existing realization
            realizations.push({
              portfolio_company_id: portfolioCompanyId,
              investment_asset_id: investmentAssetId,
              realized: false,
            })
          }
        }
      })

      // Process the other row separately
      const otherRow = document.querySelector(
        "#distribution-allocation tbody .other-row",
      )
      if (otherRow) {
        const cells = otherRow.querySelectorAll("td")
        for (let i = 1; i < cells.length; i++) {
          const inputGroup = cells[i].querySelector(".input-group")
          if (inputGroup) {
            const input = inputGroup.querySelector("input")
            const yearQuarter = inputGroup.dataset.yearQuarter
            if (input && yearQuarter) {
              const [year, quarter] = yearQuarter.split("-")
              otherBalanceSheetItems.push({
                year: parseInt(year),
                quarter: parseInt(quarter),
                amount: parseFloat(input.value || 0),
              })
            }
          }
        }
      }

      // Send data to server
      fetch(
        `/manage/net_snapshots/${this.element.dataset.netSnapshotId}/update_distributions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify({
            distributions,
            realizations,
            other_balance_sheet_items: otherBalanceSheetItems,
          }),
        },
      )
        .then((response) => {
          if (response.ok) {
            window.location.reload()
          } else {
            console.error("Failed to update distributions")
          }
        })
        .catch((error) => {
          console.error("Error updating distributions:", error)
        })

      // Toggle edit mode
      this.editModeValue = false
      this.editButtonTarget.textContent = "Edit Data"
    } else {
      // Edit mode
      this.editModeValue = true
      this.editButtonTarget.textContent = "Save Changes"
    }
  }

  editModeValueChanged() {
    if (this.editModeValue) {
      // Make cells editable
      const editableCells = document.querySelectorAll(
        '[data-net-snapshot-target="editableCell"]',
      )
      editableCells.forEach((cell) => {
        const value = parseFloat(
          cell.textContent.replace(/[^0-9.-]+/g, "") || 0,
        )
        // Preserve the checkbox if it exists, maintaining its state
        const checkbox = cell.querySelector(".realized-checkbox")
        let checkboxHtml = ""
        if (checkbox) {
          // Preserve the checkbox's classes to maintain checked/disabled state
          checkboxHtml = checkbox.outerHTML
        }
        cell.innerHTML = `<div class="input-group-text">$</div><input type="number" step="0.01" class="form-control" style="width: 150px;" value="${value}">${checkboxHtml}`
      })

      // Enable realized checkbox divs in edit mode
      const realizedCheckboxes = document.querySelectorAll(".realized-checkbox")
      realizedCheckboxes.forEach((checkbox) => {
        checkbox.style.cursor = "pointer"
        // Add event listener for realized checkbox clicks
        checkbox.addEventListener(
          "click",
          this.onRealizedCheckboxClick.bind(this),
        )
      })
    } else {
      // Revert all editable cells back to text
      const editableCells = document.querySelectorAll(
        '[data-net-snapshot-target="editableCell"]',
      )
      editableCells.forEach((cell) => {
        const input = cell.querySelector("input")
        const checkbox = cell.querySelector(".realized-checkbox")
        let checkboxHtml = ""
        if (checkbox) {
          // Preserve the checkbox's classes to maintain checked/disabled state
          checkboxHtml = checkbox.outerHTML
        }

        if (input) {
          const value = parseFloat(input.value || 0)
          cell.innerHTML =
            new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value) + checkboxHtml
        }
      })
    }
  }

  onRealizedCheckboxClick(event) {
    if (!this.editModeValue) {
      return // Only allow clicks in edit mode
    }

    const checkbox = event.currentTarget
    const row = checkbox.closest("tr")
    const isCurrentlyChecked = checkbox.classList.contains(
      "realized-checkbox--checked",
    )

    if (isCurrentlyChecked) {
      // Unchecking - remove checked class and enable all checkboxes in this row
      checkbox.classList.remove("realized-checkbox--checked")

      // Enable all checkboxes in this row
      row.querySelectorAll(".realized-checkbox").forEach((cb) => {
        cb.classList.remove("realized-checkbox--disabled")
      })

      // Remove grey background from future cells
      row.querySelectorAll("td.realized-future-cell").forEach((cell) => {
        cell.classList.remove("realized-future-cell")
      })
    } else {
      // Check if this checkbox is disabled
      if (checkbox.classList.contains("realized-checkbox--disabled")) {
        return // Don't allow clicking disabled checkboxes
      }

      // Checking - first uncheck any other checkbox in this row
      row.querySelectorAll(".realized-checkbox").forEach((cb) => {
        cb.classList.remove("realized-checkbox--checked")
      })

      // Mark this checkbox as checked
      checkbox.classList.add("realized-checkbox--checked")

      // Disable all other checkboxes in this row
      row.querySelectorAll(".realized-checkbox").forEach((cb) => {
        if (cb !== checkbox) {
          cb.classList.add("realized-checkbox--disabled")
        }
      })

      // Add grey background to all future cells (cells after this one)
      const cells = Array.from(row.querySelectorAll("td"))
      const currentCell = checkbox.closest("td")
      const currentCellIndex = cells.indexOf(currentCell)

      cells.forEach((cell, index) => {
        if (index < currentCellIndex) {
          cell.classList.add("realized-future-cell")
        } else {
          cell.classList.remove("realized-future-cell")
        }
      })
    }
  }
}
